import { useState, useCallback } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Flame, Plus, Trash2, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { WorkflowEscalationRule } from '@/types/api';

const TRIGGER_LABELS: Record<string, { label: string; color: string }> = {
  overdue: { label: 'Overdue Task', color: 'bg-compliance-critical/15 text-compliance-critical' },
  blocked: { label: 'Blocked Task', color: 'bg-compliance-high/15 text-compliance-high' },
  priority_critical: { label: 'Critical Priority', color: 'bg-compliance-critical/15 text-compliance-critical' },
  deadline_approaching: { label: 'Deadline Approaching', color: 'bg-compliance-medium/15 text-compliance-medium' },
  manual: { label: 'Manual', color: 'bg-muted text-muted-foreground' },
};

const TRIGGER_TYPES = ['overdue', 'blocked', 'priority_critical', 'deadline_approaching'] as const;

export function EscalationRulesPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['escalation-rules'],
    queryFn: () => api.escalations.rules.list(),
    enabled: !!user,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) =>
      api.escalations.rules.update(ruleId, { isActive }),
    onSuccess: () => {
      toast.success('Rule updated');
      queryClient.invalidateQueries({ queryKey: ['escalation-rules'] });
    },
    onError: () => toast.error('Failed to update rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => api.escalations.rules.delete(ruleId),
    onSuccess: () => {
      toast.success('Rule deactivated');
      queryClient.invalidateQueries({ queryKey: ['escalation-rules'] });
    },
    onError: () => toast.error('Failed to delete rule'),
  });

  const rules = data?.rules ?? [];

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/escalations')}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Escalations
        </Button>
      </div>

      <PageHeader
        title="Escalation Rules"
        subtitle="Configure automated escalation triggers"
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Rule
          </Button>
        }
      />

      {showCreate && (
        <CreateRuleForm
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['escalation-rules'] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Rules list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-card" />
          ))}
        </div>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Flame className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">No escalation rules configured.</p>
            <Button size="sm" className="mt-3" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Create First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <RuleCard
              key={rule.ruleId}
              rule={rule}
              onToggle={() => toggleMutation.mutate({ ruleId: rule.ruleId, isActive: !rule.isActive })}
              onDelete={() => deleteMutation.mutate(rule.ruleId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RuleCard({
  rule,
  onToggle,
  onDelete,
}: {
  readonly rule: WorkflowEscalationRule;
  readonly onToggle: () => void;
  readonly onDelete: () => void;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const closeDelete = useCallback(() => setShowDeleteConfirm(false), []);
  const deleteRef = useFocusTrap(showDeleteConfirm, closeDelete);
  const trigger = TRIGGER_LABELS[rule.triggerType] ?? { label: rule.triggerType, color: 'bg-muted text-muted-foreground' };

  return (
    <Card className={cn(!rule.isActive && 'opacity-60')}>
      <CardContent className="flex items-center gap-4 p-4">
        <button
          className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          onClick={onToggle}
          title={rule.isActive ? 'Deactivate' : 'Activate'}
        >
          {rule.isActive ? (
            <ToggleRight className="h-5 w-5 text-teal" />
          ) : (
            <ToggleLeft className="h-5 w-5" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{rule.name}</p>
            <Badge variant="outline" className={cn('text-[10px]', trigger.color)}>
              {trigger.label}
            </Badge>
            {!rule.isActive && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                Inactive
              </Badge>
            )}
          </div>
          {rule.description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{rule.description}</p>
          )}
          <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span>Escalate to: <strong>{rule.escalateToRole}</strong></span>
            {rule.escalateToUser && <span>User: {rule.escalateToUser.slice(0, 8)}</span>}
            {rule.notifyChannels.length > 0 && (
              <span>Notify: {rule.notifyChannels.join(', ')}</span>
            )}
            {Object.keys(rule.triggerConfig).length > 0 && (
              <span>Config: {JSON.stringify(rule.triggerConfig)}</span>
            )}
          </div>
        </div>

        {showDeleteConfirm ? (
          <div ref={deleteRef} role="dialog" aria-label="Delete rule confirmation" className="flex items-center gap-1.5 rounded border border-compliance-critical/30 bg-compliance-critical/5 px-2 py-1">
            <span className="text-[10px] font-medium text-compliance-critical">Delete?</span>
            <Button
              variant="outline"
              size="sm"
              className="h-5 px-2 border-compliance-critical/50 text-[10px] text-compliance-critical hover:bg-compliance-critical hover:text-white"
              onClick={() => { onDelete(); setShowDeleteConfirm(false); }}
            >
              Yes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-2 text-[10px]"
              onClick={() => setShowDeleteConfirm(false)}
            >
              No
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-1.5 text-muted-foreground hover:text-compliance-critical"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function CreateRuleForm({
  onCreated,
  onCancel,
}: {
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<string>('overdue');
  const [escalateToRole, setEscalateToRole] = useState('cco');
  const [thresholdHours, setThresholdHours] = useState('24');

  const createMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.escalations.rules.create(input),
    onSuccess: () => {
      toast.success('Rule created');
      onCreated();
    },
    onError: () => toast.error('Failed to create rule'),
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    const triggerConfig: Record<string, unknown> = {};
    if (triggerType === 'overdue' || triggerType === 'deadline_approaching') {
      triggerConfig.thresholdHours = parseInt(thresholdHours, 10);
    }

    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      triggerType,
      triggerConfig,
      escalateToRole,
      notifyChannels: ['in_app'],
    });
  };

  return (
    <Card className="border-teal/30">
      <CardContent className="p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">New Escalation Rule</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Overdue Task Alert"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Trigger Type</label>
            <Select value={triggerType} onValueChange={(v) => setTriggerType(v ?? 'overdue')}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGER_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TRIGGER_LABELS[t]?.label ?? t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Escalate To Role</label>
            <Select value={escalateToRole} onValueChange={(v) => setEscalateToRole(v ?? 'cco')}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cco">CCO</SelectItem>
                <SelectItem value="compliance_rep">Compliance Rep</SelectItem>
                <SelectItem value="department_rep">Department Rep</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(triggerType === 'overdue' || triggerType === 'deadline_approaching') && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Threshold (hours)
              </label>
              <Input
                type="number"
                value={thresholdHours}
                onChange={(e) => setThresholdHours(e.target.value)}
                min={1}
                className="mt-1"
              />
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Rule'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
